<script setup lang="ts">
import {
    FileText,
    Loader2,
    MessageCircleMore,
    Presentation,
    Search
} from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { debounce } from '@/lib/commonFunctions';
import { getTimeGroup } from '@/lib/sortFunctions';
import { searchChatSessions } from '@/services/chat_sessions';
import { useDocumentsStore } from '@/stores/documentsStore';
import { useTenantStore } from '@/stores/tenant';
import type { ChatSessionSearchResultItem } from '@/types';

const isOpen = defineModel<boolean>('open', { default: false });

const router = useRouter();
const tenantStore = useTenantStore();
const documentsStore = useDocumentsStore();

const LIMIT = 10;

const searchQuery = ref('');
const results = ref<ChatSessionSearchResultItem[]>([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(false);
const focusedIndex = ref(-1);

let abortController: AbortController | null = null;

const documentsMap = computed(
    () => new Map(documentsStore.documents.map((d) => [d.id, d]))
);

type EnrichedResult = ChatSessionSearchResultItem & {
    name: string;
    icon: typeof Presentation | typeof MessageCircleMore;
    link: string;
    dateLabel: string | null;
};

const enrichedResults = computed<EnrichedResult[]>(() =>
    results.value.map((item) => {
        const doc = item.chat_session.document_id
            ? documentsMap.value.get(item.chat_session.document_id)
            : undefined;
        const name = doc?.name ?? item.chat_session.name;
        const icon =
            item.chat_session.document_id &&
            (doc?.document_type === 'slides' || doc?.document_type === 'pptx')
                ? Presentation
                : item.chat_session.document_id && doc?.document_type === 'docx'
                  ? FileText
                  : MessageCircleMore;
        const link =
            item.chat_session.document_id && doc
                ? `/viewer?chatId=${item.chat_session.id}&fileId=${item.chat_session.document_id}&fileType=${doc.document_type}`
                : `/chat/${item.chat_session.id}`;
        const raw = item.chat_session.updated_at;
        let dateLabel: string | null = null;
        if (raw) {
            const label = getTimeGroup(new Date(raw));
            dateLabel = label === 'undefined' ? null : label;
        }
        return { ...item, name, icon, link, dateLabel };
    })
);

// --- Fetch ---

async function fetchResults(query?: string) {
    if (!tenantStore.tenantId) return;
    if (abortController) abortController.abort();
    abortController = new AbortController();
    const signal = abortController.signal;

    isLoading.value = true;
    try {
        const data = await searchChatSessions(
            tenantStore.tenantId,
            { search: query, limit: LIMIT, offset: 0 },
            signal
        );
        results.value = data.items;
        hasMore.value = data.has_more;
        isLoading.value = false;
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        isLoading.value = false;
        throw error;
    }
}

async function loadMore() {
    if (!tenantStore.tenantId || isLoadingMore.value) return;
    const signal = abortController?.signal;
    isLoadingMore.value = true;
    try {
        const data = await searchChatSessions(
            tenantStore.tenantId,
            {
                search: searchQuery.value || undefined,
                limit: LIMIT,
                offset: results.value.length
            },
            signal
        );
        if (signal?.aborted) return;
        results.value = [...results.value, ...data.items];
        hasMore.value = data.has_more;
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        throw error;
    } finally {
        isLoadingMore.value = false;
    }
}

const debouncedFetch = debounce((query: string) => {
    fetchResults(query || undefined);
}, 600);

// --- Watchers ---

// Prevents the searchQuery watcher from firing when the query is reset on open,
// which would cause a duplicate request on top of the one triggered directly.
// Only set when searchQuery is non-empty, otherwise no change event fires and
// the flag would stay true, swallowing the user's first keypress.
let skipQueryWatch = false;

watch(isOpen, (open) => {
    if (!open) debouncedFetch.cancel();
    if (open) {
        if (searchQuery.value !== '') skipQueryWatch = true;
        searchQuery.value = '';
        focusedIndex.value = -1;
        fetchResults();
    }
});

watch(searchQuery, (query) => {
    if (skipQueryWatch) {
        skipQueryWatch = false;
        return;
    }
    focusedIndex.value = -1;
    debouncedFetch(query);
});

// --- Snippet highlight ---

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getSearchHighlightTerms(raw: string): string[] {
    const terms = raw
        .split(/\s+/)
        .map((w) => w.trim())
        .filter(Boolean);
    return [...new Set(terms)].sort((a, b) => b.length - a.length);
}

function getSnippetParts(text: string): { text: string; highlight: boolean }[] {
    const query = searchQuery.value.trim();
    if (!query) return [{ text, highlight: false }];

    const terms = getSearchHighlightTerms(query);
    if (terms.length === 0) return [{ text, highlight: false }];

    const pattern = terms.map(escapeRegExp).join('|');
    if (!pattern) return [{ text, highlight: false }];

    const parts: { text: string; highlight: boolean }[] = [];
    let lastIndex = 0;
    for (const match of text.matchAll(new RegExp(pattern, 'gi'))) {
        const start = match.index ?? 0;
        if (start > lastIndex) {
            parts.push({
                text: text.slice(lastIndex, start),
                highlight: false
            });
        }
        parts.push({ text: match[0], highlight: true });
        lastIndex = start + match[0].length;
    }
    if (lastIndex < text.length) {
        parts.push({ text: text.slice(lastIndex), highlight: false });
    }
    return parts;
}

// --- Actions ---

function selectSession(item: EnrichedResult) {
    isOpen.value = false;
    router.push(item.link);
}

function handleKeydown(e: KeyboardEvent) {
    const total = enrichedResults.value.length;
    if (!total) return;

    if (e.key === 'ArrowDown') {
        e.preventDefault();
        focusedIndex.value = Math.min(focusedIndex.value + 1, total - 1);
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        focusedIndex.value = Math.max(focusedIndex.value - 1, 0);
    } else if (e.key === 'Enter' && focusedIndex.value >= 0) {
        e.preventDefault();
        selectSession(enrichedResults.value[focusedIndex.value]);
    }
}
</script>

<template>
    <Dialog v-model:open="isOpen">
        <DialogContent class="max-w-2xl p-4 gap-0 overflow-hidden">
            <div class="flex items-center gap-2 pl-4 pr-8">
                <Search
                    class="h-4 w-4 shrink-0 text-muted-foreground pointer-events-none"
                />
                <Input
                    v-model="searchQuery"
                    placeholder="Search in chats"
                    class="border-none focus-visible:ring-0 focus-visible:ring-offset-0 text-md"
                    autofocus
                    @keydown="handleKeydown"
                />
            </div>
            <Separator class="my-4" />

            <ScrollArea class="h-80">
                <div class="py-1">
                    <div
                        v-if="isLoading"
                        class="flex items-center justify-center py-8 text-muted-foreground"
                    >
                        <Loader2 class="h-5 w-5 animate-spin" />
                    </div>

                    <p
                        v-else-if="enrichedResults.length === 0"
                        class="text-center text-sm text-muted-foreground py-8"
                    >
                        No chats found
                    </p>

                    <template v-else>
                        <Button
                            v-for="(item, index) in enrichedResults"
                            :key="item.chat_session.id"
                            variant="ghost"
                            class="w-full justify-start px-4 py-2 h-auto flex flex-col items-start mb-1 transition-all hover:bg-primary10"
                            :class="{ 'bg-primary10': index === focusedIndex }"
                            @click="selectSession(item)"
                            @mouseenter="focusedIndex = index"
                        >
                            <div
                                class="flex items-center justify-between gap-2 w-full"
                            >
                                <div class="flex items-center gap-2">
                                    <component
                                        :is="item.icon"
                                        class="h-4 w-4 shrink-0 text-muted-foreground"
                                    />
                                    <p
                                        class="truncate text-sm font-normal flex-1"
                                    >
                                        {{ item.name }}
                                    </p>
                                </div>
                                <p
                                    v-if="item.dateLabel"
                                    class="text-2xs font-normal shrink-0 text-muted-foreground"
                                >
                                    {{ item.dateLabel }}
                                </p>
                            </div>
                            <p
                                v-if="item.snippet"
                                class="text-xs text-muted-foreground truncate w-full pl-6 text-left"
                            >
                                <span
                                    v-for="(part, i) in getSnippetParts(
                                        item.snippet
                                    )"
                                    :key="i"
                                    :class="
                                        part.highlight
                                            ? 'bg-yellow25 dark:bg-yellowContrast25 dark:text-gray-800 rounded-sm'
                                            : ''
                                    "
                                    >{{ part.text }}</span
                                >
                            </p>
                        </Button>

                        <Button
                            v-if="hasMore"
                            variant="ghost"
                            class="w-full text-xs text-muted-foreground hover:bg-transparent hover:underline"
                            :disabled="isLoadingMore"
                            @click="loadMore"
                        >
                            <Loader2
                                v-if="isLoadingMore"
                                class="h-4 w-4 animate-spin mr-2"
                            />
                            Load more
                        </Button>
                    </template>
                </div>
            </ScrollArea>
        </DialogContent>
    </Dialog>
</template>
